/** @jsx jsx */

import { Block } from 'factories/block';
import { Stream } from '@cloudflare/stream-react';
import { css, jsx } from '@emotion/core';
import playVideo from 'assets/playVideo.svg';
import VideoIcon from 'assets/Video.svg';
import { interactionContext } from 'components/lib/TextAndImage/interactionContext';
import { InteractionContext as interactionContextFromContainerLogical } from 'components/Layouts/ContainerLogical';
import EditMarker from 'components/UI/EditMarker';
import { keyframes } from '@emotion/core';
import useIntersectionObserver from 'hooks/useIntersectionObserver';
import {
  Fragment as _,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  ReactElement,
} from 'react';
import ReactPlayer from 'react-player';
import setCurrentBlock from 'services/apiHelpers/setCurrentBlock';
import { killAllAudio } from 'services/killAllAudio';
import { unlockInteraction } from 'services/unlockInteraction';
import { colors, getButtonFocus } from 'styles/variables';
import Transcript from './Transcript';
import { HostingContext } from 'hosting/context';
import {
  videoPlayEvent,
  videoSeekEvent,
  videoPauseEvent,
  videoFinishEvent,
} from 'hosting/events';
import { useSelector } from 'react-redux';
import { State } from 'store/store';
import pubSub from 'services/pubSub';

const { lPink } = colors;

const frameSelected = css`
  from {
    width: 50%;
    height: 50%;
  }
  to {
    width: 75%;
    height: 75%;
  }
`;

const ShowTranscriptBtn = ({
  id,
  setShowTranscript,
  showTranscriptFontSize = 12,
  showTranscriptFontColor = '#000',
  showTranscriptLabel = 'Show transcript',
}) => {
  return (
    <button
      id={`show-transcript-${id}`}
      css={css`
        width: 100%;
        font-size: ${showTranscriptFontSize}px;
        border: none;
        outline: none;
        background: transparent;
        text-decoration: underline;
        text-align: center;
        cursor: pointer;
        color: ${showTranscriptFontColor};
        &:focus {
          ${getButtonFocus('#2684FF')}
        }
      `}
      onClick={() => {
        setShowTranscript(true);
      }}
    >
      {showTranscriptLabel}
    </button>
  );
};

const EditWrapper = ({ id, selected }) => {
  return (
    <div
      css={css`
        position: absolute;
        display: flex;
        justify-content: center;
        align-items: center;
        left: 0px;
        top: 0px;
        width: 100%;
        height: 100%;
        pointer-events: none;
      `}
    >
      <div
        css={css`
                position: relative;
                display: flex;
                justify-content: center;
                align-items: center;
                width: ${selected ? '75%' : '50%'};
                height: ${selected ? '75%' : '50%'};
                ${selected && `animation: ${frameSelected} 0.2s forwards;`}
                ${!selected &&
                  `
                background: rgba(0, 0, 0, 0.1);
                `}
                box-shadow: ${
                  selected ? `0 0 0 1px ${lPink}` : '0 0 0 1px lightGrey'
                };
                transition: all 0.2s;
                pointer-events: all;
                cursor: pointer;
                &:hover {
                  ${!selected && `box-shadow: 0 0 0 1px ${lPink};`}
                }
            `}
        onClick={e => {
          e.stopPropagation();
          if (selected) return;
          setCurrentBlock({ id });
        }}
      >
        {!selected && (
          <img src={VideoIcon} style={{ width: 50, opacity: 0.5 }} />
        )}

        {!selected && <EditMarker />}
      </div>
    </div>
  );
};

type VideoProps = {
  id: string | any;
  src: string;
  videoSrc: string;
  maxWidth: number;
  hideControls: boolean;
  useTranscript: boolean;
  showTranscriptFontSize: number;
  showTranscriptFontColor: string;
  showTranscriptLabel: string;
  viewingTranscriptUnlocksInteraction: boolean;
  cloudflareUid: string;
  posterSrc: string;
  focusColor: string;
  autoPlay: boolean;
  playInline: boolean;
  isSquare: boolean;
  presetCustomAspectRatio: 'preset' | 'custom';
  aspectRatio: string;
  vWidth: number;
  vHeight: number;
  children: ReactElement;
};

function getAspectRatio(width, height) {
  const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
  const divisor = gcd(width, height);
  return `${width / divisor}/${height / divisor}`;
}

const Video = (props: VideoProps) => {
  const { entry, init } = useIntersectionObserver(true);

  const {
    id,
    src,
    videoSrc,
    maxWidth = 600,
    useTranscript,
    showTranscriptFontSize = 12,
    showTranscriptFontColor = '#000',
    showTranscriptLabel = 'Show transcript',
    viewingTranscriptUnlocksInteraction = false,
    posterSrc,
    presetCustomAspectRatio = 'preset',
    vWidth,
    vHeight,
    focusColor = '#000',
    children,
  } = props;

  // for local testing of the ReactPlayer when we have a video coming from cloudFlare
  //

  let cloudflareUid = props?.cloudflareUid;
  // nix the Uid
  // cloudflareUid = null;

  let aspectRatio;

  if (presetCustomAspectRatio === 'preset') {
    aspectRatio = props?.aspectRatio;
  } else {
    if (vWidth && vHeight) {
      aspectRatio = getAspectRatio(vWidth, vHeight);
    }
  }

  // defaults
  let { hideControls = false, autoPlay = false, playInline = false } = props;

  // forced false in dev
  if (window.dev()) {
    hideControls = false;
    autoPlay = false;
    playInline = false;
  }

  const isYouTube = (videoSrc || src).match(
    /^(https?\:\/\/)?((www\.)?youtube\.com|youtu\.be)\/.+$/
  );

  // we won't use this for the moment
  // here as a utility for if needed later
  //
  // const isVimeo = (videoSrc || src).match(
  //   /^(https?:\/\/)?(www\.)?vimeo\.com\/(channels\/[a-zA-Z0-9_-]+\/)?([0-9]+)(\/[a-zA-Z0-9_-]+)?(\?t=[0-9]+s)?$|^https?:\/\/player\.vimeo\.com\/video\/([0-9]+)(\/[a-zA-Z0-9_-]+)?(\?h=[a-zA-Z0-9]+)?(\?t=[0-9]+s)?$/
  // );

  const [isPlaying, setIsPlaying] = useState(false);
  const [hasPlayedVideoWithPoster, setHasPlayedVideoWithPoster] = useState(
    false
  );
  const [calcHeight, setCalcHeight] = useState(maxWidth * 0.5625);
  const [showTranscript, setShowTranscript] = useState(false);
  const highContrast = useSelector((state: State) => {
    return state?.theme?.highContrast;
  });
  const focusedNodeId = useSelector((state: State) => {
    return state.focusedNodeId;
  });
  const selected = focusedNodeId === id;
  const context = (useContext(interactionContext) ||
    useContext(interactionContextFromContainerLogical)) as Block;

  // we should only unlock interaction when in the basic TextAndImage context
  // otherwise we might unlock interaction early. and that'd be a wrongness
  const shouldUnlockInteraction = context?.meta?.subType === 'TextAndImage';

  // let's grab the context id explicitly;
  const { id: contextId } = context || {};
  const hostingCtx = useContext(HostingContext);
  const mounted = useRef(false);
  const video = useRef<HTMLDivElement>(null);
  // need to get the correct type for player
  const player = useRef<any>(null);
  const hasAutoPlayed = useRef<boolean>(false);

  useLayoutEffect(() => {
    if (!mounted.current) {
      init([video.current], {
        threshold: 0.5,
      });
    }

    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const pS = pubSub.subscribe('KILL_ALL_PLAYING_VIDEO', ({ _id_ }) => {
      //
      if (id !== _id_) {
        //
        cloudflareUid && player?.current?.pause();
        setIsPlaying(false);
      }
    });

    return pS;
  });

  useEffect(() => {
    const onWindowResize = () => {
      const { width } = video.current!.getBoundingClientRect();

      if (aspectRatio) {
        const [a, b] = aspectRatio?.split('/') || [];

        setCalcHeight(width * (b / a));
        return;
      }

      const calcHeightTarget = width * 0.5625;

      if (calcHeight === calcHeightTarget) {
        return;
      }

      setCalcHeight(calcHeightTarget);
    };

    onWindowResize();
    window.addEventListener('resize', onWindowResize);

    return () => {
      window.removeEventListener('resize', onWindowResize);
    };
  });

  useEffect(() => {
    // pauses cloudflare player
    //
    if (!cloudflareUid || !player.current) {
      return;
    }

    if (isPlaying && entry && !entry.isIntersecting) {
      player.current.pause();
      //
      hostingCtx?.data(videoPauseEvent(contextId, id));
    }
  }, [entry?.isIntersecting]);

  useEffect(() => {
    if (isPlaying) {
      killAllAudio();
      //
      hostingCtx?.data(videoPlayEvent(contextId, id));
    }
  }, [isPlaying]);

  useEffect(() => {
    const onFullScreenChange = e => {
      if (!document.fullscreenElement && video?.current) {
        // this specific selector fails when video is added as media to interactions NOT
        // a standalone video interaction
        // const section = document.querySelector(`#i${contextId}`);

        // let's be more generic and get whichever section element is closest to the video
        const section = video.current.closest('section');
        section?.scrollIntoView();
      }
    };

    video?.current?.addEventListener('fullscreenchange', onFullScreenChange);

    return () => {
      video?.current?.removeEventListener(
        'fullscreenchange',
        onFullScreenChange
      );
    };
  }, []);

  useEffect(() => {
    if (
      shouldUnlockInteraction &&
      viewingTranscriptUnlocksInteraction &&
      showTranscript
    ) {
      unlockInteraction(contextId);
    }
  }, [showTranscript]);

  useEffect(() => {
    if (!autoPlay) {
      return;
    }

    if (entry?.isIntersecting && !hasAutoPlayed.current) {
      // console.log('browser allows autoPlay !?!?');
      //

      cloudflareUid ? player?.current?.play() : setIsPlaying(true);
      hasAutoPlayed.current = true;
    }
  }, [entry?.isIntersecting]);

  useEffect(() => {
    if (entry?.isIntersecting) {
      return;
    }

    // reset the hasAutoPlayed
    if (autoPlay) {
      hasAutoPlayed.current = false;
    }
  }, [entry?.isIntersecting]);

  const continuePlayingInFullscreen =
    isPlaying && document.fullscreenElement !== null ? true : false;

  // if we have an aspectRatio. videoHeight will be 100% the height of video-player-container
  // if not we set it to the ' default ' calcHeight which has been the height for all times
  // ( so no change to existing )
  // if none of these conditions we'll fallBack to 100%
  //
  const videoHeight = aspectRatio ? '100%' : calcHeight || '100%';

  let isOneColumnLayout = context?.meta?.layout?.toLowerCase()?.includes('one');

  const videoPlayer = (
    <div
      ref={video}
      className="video-player-container"
      css={css`
        position: relative;
        width: 100%;
        max-width: ${maxWidth}px;
        aspect-ratio: ${aspectRatio};
        overflow: hidden;
        //
      `}
      //
    >
      <_>
        {cloudflareUid ? (
          <CloudflareVideo
            streamRef={player}
            id={id}
            cloudflareUid={cloudflareUid}
            width={'100%'}
            height={videoHeight}
            onPlay={() => {
              // console.log('CLOUDFLARE PLAYER PLAYS. . .');
              pubSub.publish('KILL_ALL_PLAYING_VIDEO', { _id_: id });
              //
              setIsPlaying(true);
              //
              hostingCtx?.data(videoPlayEvent(contextId, id));
            }}
            onSeek={() => {
              hostingCtx?.data(videoSeekEvent(contextId, id));
            }}
            onPause={() => {
              setIsPlaying(false);
              //
              hostingCtx?.data(videoPauseEvent(contextId, id));
            }}
            onEnded={() => {
              shouldUnlockInteraction && unlockInteraction(contextId);
              //
              hostingCtx?.data(videoFinishEvent(contextId, id));
            }}
            controls={!hideControls}
            poster={posterSrc}
            {...(playInline && {
              autoplay: true,
              loop: true,
              muted: true,
              playsinline: true,
              controls: false,
            })}
            {...(entry?.isIntersecting &&
              autoPlay && {
                autoplay: true,
              })}
          />
        ) : (
          <_>
            {/* @ts-ignore */}
            <ReactPlayer
              ref={player}
              url={videoSrc || src}
              config={{
                vimeo: {
                  playerOptions: {
                    // if we load the maxWidth option when in 2 column
                    // content jumps and compresses the text in the other column
                    //
                    width: isOneColumnLayout ? `${maxWidth}px` : 'auto',
                  },
                },
              }}
              width="100%"
              height={videoHeight}
              onReady={() => {
                // console.log('VIDEO IS READY !!!');
              }}
              onStart={() => {
                // console.log('VIDEO HAS STARTED !!!');
              }}
              onPlay={() => {
                // console.log('REACT PLAYER PLAYS. . .');
                pubSub.publish('KILL_ALL_PLAYING_VIDEO', { _id_: id });
                //
                setIsPlaying(true);
                //
                hostingCtx?.data(videoPlayEvent(contextId, id));
              }}
              onSeek={() => {
                hostingCtx?.data(videoSeekEvent(contextId, id));
              }}
              onPause={() => {
                setIsPlaying(false);
                //
                hostingCtx?.data(videoPauseEvent(contextId, id));
              }}
              onEnded={() => {
                shouldUnlockInteraction && unlockInteraction(contextId);
                //
                hostingCtx?.data(videoFinishEvent(contextId, id));
              }}
              playing={
                continuePlayingInFullscreen ||
                (isPlaying && entry && entry.isIntersecting)
              }
              controls={!hideControls}
              light={posterSrc}
              {...(playInline && {
                playing: true,
                loop: true,
                muted: true,
                playsinline: true,
                controls: false,
              })}
            />
          </_>
        )}
      </_>

      {!window.dev() && (
        <_>
          {hideControls && !isYouTube && (
            <div
              style={{
                position: 'absolute',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                left: 0,
                top: 0,
                width: '100%',
                height: '100%',
                background: 'transparent',
                cursor: 'pointer',
              }}
              onClick={() => {
                hasAutoPlayed.current = true;

                if (cloudflareUid) {
                  // play for cloudflare. . .
                  player?.current[isPlaying ? 'pause' : 'play']();
                  return;
                }

                setIsPlaying(isPlaying => !isPlaying);
              }}
            >
              {!isPlaying && (
                <div
                  className="fader_-2s"
                  style={{
                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    border: '2px solid #000',
                    background: `url('${playVideo}') no-repeat`,
                    backgroundPosition: 'center',
                    boxShadow: '0 0 10px rgba(0,0,0, 0.2)',
                  }}
                />
              )}
            </div>
          )}
        </_>
      )}

      {!window.dev() &&
        !cloudflareUid &&
        posterSrc &&
        !hasPlayedVideoWithPoster && (
          <button
            onClick={() => {
              if (!hasPlayedVideoWithPoster) {
                //
                // this is to 'manually' let ReactPlayer know we've clicked the poster
                // and it should respond appropriately
                player?.current?.handleClickPreview();
                //
                //
                setHasPlayedVideoWithPoster(true);
                setIsPlaying(true);
                //
              }
            }}
            css={css({
              all: 'unset',
              position: 'absolute',
              left: -2,
              top: -2,
              width: 'calc(100% + 4px)',
              height: 'calc(100% + 4px)',
              boxShadow: '0 0 0 4px rgba(0, 0, 0, 0)',
              cursor: 'pointer',
              background: 'rgba(255, 255, 255, 0)',
              ':hover': {
                background: 'rgba(255, 255, 255, 0.05)',
              },
              ':focus-visible': {
                boxShadow: `0 0 0 ${highContrast ? 3 : 1}px ${focusColor}`,
              },
              transition: 'background 0.1s, box-shadow 0.2s',
            })}
          />
        )}

      {window.dev() && <EditWrapper id={id} selected={selected} />}
    </div>
  );

  const showTranscriptBtn = (
    <div style={{ margin: '5px 0 0 0' }}>
      <ShowTranscriptBtn
        id={id}
        setShowTranscript={setShowTranscript}
        showTranscriptFontSize={showTranscriptFontSize}
        showTranscriptFontColor={showTranscriptFontColor}
        showTranscriptLabel={showTranscriptLabel}
      />
    </div>
  );

  return (
    <_>
      {window.dev() ? (
        <div
          css={css`
            position: relative;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            left: 0px;
            top: 0px;
            width: 100%;
          `}
        >
          {videoPlayer}

          {useTranscript && showTranscriptBtn}
        </div>
      ) : (
        <div
          css={css`
            position: relative;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            width: 100%;
            height: 100%;
          `}
        >
          {videoPlayer}

          {useTranscript && showTranscriptBtn}
        </div>
      )}

      {showTranscript && (
        <Transcript
          onClose={() => {
            const tick = setInterval(() => {
              const showTranscriptButtonElement: any = document.querySelector(
                `#show-transcript-${id}`
              );

              if (showTranscriptButtonElement) {
                showTranscriptButtonElement.focus();

                clearInterval(tick);
              }
            }, 0);

            setShowTranscript(false);
          }}
        >
          {children}
        </Transcript>
      )}
    </_>
  );
};

export const CloudflareVideo = props => {
  const { id, cloudflareUid, ...rest } = props;
  const { height } = rest;

  return (
    <div
      css={css({
        height: height,
        '*': {
          maxHeight: height,
        },
      })}
    >
      {/* @ts-ignore */}
      <Stream
        controls
        src={cloudflareUid}
        {...{
          ...rest,
          //
        }}
      />
    </div>
  );
};

export default Video;
